import { Editor, Node, NodeEntry, Range, Path } from 'slate';
import { find } from 'linkifyjs';
import {
  HASHTAG_REGEX,
  ASCII_REGEX,
  HASHTAG_MAX,
} from 'js/social/shared/SocialEditor/utils';

type Segment = [number, number];

/**
 * Given a sorted set of current Slate ranges over text, returns
 * a list of segments that do not overlap those ranges.
 * @param ranges The ranges to filter out.
 * @param text The text to put the ranges on.
 */
export function getFreeSegments(ranges: Range[], text: string) {
  const segments: Segment[] = [];

  let start = 0;
  for (const range of ranges) {
    const end = range.anchor.offset;
    if (end - start > 0) {
      segments.push([start, end]);
    }
    start = range.focus.offset;
  }
  if (start < text.length) {
    segments.push([start, text.length]);
  }

  return segments;
}

/**
 * Given a leaf node, find the ranges of link text within the node.
 * @param path The path to the node.
 * @param text The text of the node.
 */
export function findLinkRanges(path: Path, text: string, readOnly?: boolean) {
  const ranges: Range[] = [];
  for (const match of text.matchAll(ASCII_REGEX)) {
    const matchStart = match.index ?? 0;
    ranges.push(
      ...find(match[0])
        .filter(({ isLink }) => isLink)
        .map(({ href, start, end }) => ({
          anchor: {
            path,
            offset: matchStart + start,
          },
          focus: {
            path,
            offset: matchStart + end,
          },
          link: href,
          readOnly,
        })),
    );
  }
  return ranges;
}

/**
 * Given a leaf node, find the ranges of hashtag text
 * within the node, ignoring `blockingRanges`
 * @param path The path to the node.
 * @param text The text of the node.
 * @param ignoreRanges The ranges to ignore.
 * @param allowedHashtags Which hashtags to allow. If undefined, any are allowed.
 */
export function findHashtagRanges(
  path: Path,
  text: string,
  ignoreRanges: Range[],
  allowedHashtags?: string[],
  readOnly?: boolean,
) {
  const validHashtags = allowedHashtags ? new Set(allowedHashtags) : undefined;
  const hashtags = new Set<string>();
  const ranges: Range[] = [];
  for (const [start, end] of getFreeSegments(ignoreRanges, text)) {
    const subText = text.substring(start, end);
    for (const match of subText.matchAll(HASHTAG_REGEX)) {
      const hashtag = match[0].toLowerCase().substring(1);
      const valid = validHashtags ? validHashtags.has(hashtag) : true;
      if (valid && hashtags.size < HASHTAG_MAX && !hashtags.has(hashtag)) {
        hashtags.add(hashtag);
        const matchStart = match.index ?? 0;
        const matchEnd = matchStart + match[0].length;
        ranges.push({
          anchor: {
            path,
            offset: start + matchStart,
          },
          focus: {
            path,
            offset: start + matchEnd,
          },
          // @ts-ignore
          hashtag: true,
          readOnly,
        });
      }
    }
  }
  return ranges;
}

/**
 * Slate decorator callback that uses linkifyjs to
 * find links and annotate leaf nodes with information
 * about those links. It also uses regular expressions
 * to find hashtags and annotate leaf nodes.
 * @param editor The editor to search.
 * @param allowedHashtags Which hashtags to allow. If undefined, any are allowed.
 * @param readOnly If the editor is in read-only mode.
 */
export function decorate(
  editor: Editor,
  allowedHashtags?: string[],
  readOnly?: boolean,
) {
  return ([node, path]: NodeEntry) => {
    try {
      // We only want to decorate leaves (raw text)
      Node.leaf(editor, path);

      const text = Node.string(node);
      const ranges = findLinkRanges(path, text, readOnly);
      ranges.push(...findHashtagRanges(path, text, ranges, allowedHashtags));

      return ranges;
    } catch {
      return [];
    }
  };
}
